import React from 'react';
import { LessonItem, StepBodyEntry } from './types';
import RichText from './RichText';
import VideoEmbed from './VideoEmbed';

// Declared ahead of Item because the two are mutually recursive: a step's body
// holds items, and one of the item kinds is a step.
// eslint-disable-next-line @typescript-eslint/no-use-before-define
function StepBody({ entry }: { entry: StepBodyEntry }) {
  if (typeof entry === 'string') return <p className="pc-lesson-p">{entry}</p>;
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  return <Item item={entry} />;
}

// Items are discriminated by an `is*` flag, so narrowing goes through `in`.
function Item({ item }: { item: LessonItem }) {
  if ('isParagraph' in item) return <p className="pc-lesson-p">{item.text}</p>;
  if ('isText' in item) return <p className="pc-lesson-p">{item.text}</p>;
  if ('isSubheading' in item) return <h3 className="pc-lesson-subheading">{item.text}</h3>;

  if ('isBullet' in item) {
    return (
      <div className="pc-lesson-bullet">
        <span className="pc-lesson-bullet-dot" aria-hidden="true">•</span>
        <span>{item.text}</span>
      </div>
    );
  }

  if ('isLabelBullet' in item) {
    return (
      <div className="pc-lesson-bullet">
        <span className="pc-lesson-bullet-dot" aria-hidden="true">•</span>
        <span>
          <span className="pc-lesson-bullet-label">{`${item.label}.`}</span>
          {` ${item.text}`}
        </span>
      </div>
    );
  }

  if ('isStep' in item) {
    return (
      <div className="pc-lesson-step">
        <span className="pc-lesson-step-num">{item.number}</span>
        <div className="pc-lesson-step-body">
          {item.stepTitle && <h4 className="pc-lesson-step-title">{item.stepTitle}</h4>}
          {item.stepBody.map((entry, i) => (
            /* eslint-disable-next-line react/no-array-index-key */
            <StepBody key={i} entry={entry} />
          ))}
        </div>
      </div>
    );
  }

  if ('isVideo' in item) return item.hasVideo ? <VideoEmbed url={item.embedUrl} /> : null;
  if ('isImage' in item) return <img className="pc-lesson-img" src={item.src} alt={item.alt} />;
  if ('isRichText' in item) return <RichText spans={item.spans} />;
  // Unwrap rather than assume the payload's kind — it is rich text in most
  // lessons and an image in others.
  if ('isNode' in item) return <Item item={item.node} />;
  return null;
}

export default function Items({ items }: { items: LessonItem[] }) {
  return (
    <>
      {items.map((item, i) => (
        /* eslint-disable-next-line react/no-array-index-key */
        <Item key={i} item={item} />
      ))}
    </>
  );
}
